import React, {useState, useEffect} from 'react';
import axios from 'axios';
import { getCSRFToken } from '../utils/csrf';
import {DndProvider} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import DroppableGroup from './DroppableGroup';
import UnassignedStudents from './UnassignedStudents';
import RemoveGroup from './RemoveGroup';
import GroupImportModal from './GroupImportModal';

import './Groups.css';

const ManageGroups = () => {
    const [gradeLevels, setGradeLevels] = useState([]);
    const [selectedGradeLevel, setSelectedGradeLevel] = useState('');
    const [currentGroupType, setCurrentGroupType] = useState('core'); // Track current group type (core, cte, homeroom)
    const [groupData, setGroupData] = useState({
        core: {groups: [], unassignedStudents: []},
        cte: {groups: [], unassignedStudents: []},
        homeroom: {groups: [], unassignedStudents: []},
    });
    const [newGroupName, setNewGroupName] = useState(''); // For manual group creation
    const [showImportModal, setShowImportModal] = useState(false); // For import modal

    // Fetch grade levels on component mount
    useEffect(() => {
        axios.get('/courses/api/grade_levels/')
            .then(response => setGradeLevels(response.data))
            .catch(error => console.error('Error fetching grade levels:', error));
    }, []);


    // Fetch grade level data for new group models
    const fetchGradeLevelData = async (gradeLevel, groupType) => {
        console.log(`Fetching data for gradeLevel=${gradeLevel}, groupType=${groupType}`);

        if (!gradeLevel) {
            console.warn("Skipping API call: No grade level selected.");
            return;
        }

        try {
            let endpoint;
            
            // Use the new group model endpoints
            switch (groupType) {
                case 'core':
                    endpoint = `/api/groups/core-groups/grade-level-data/${gradeLevel}/`;
                    break;
                case 'cte':
                    endpoint = `/api/groups/cte-groups/grade-level-data/${gradeLevel}/`;
                    break;
                case 'homeroom':
                    endpoint = `/api/groups/homeroom-groups/grade-level-data/${gradeLevel}/`;
                    break;
                default:
                    console.error(`Unknown group type: ${groupType}`);
                    return;
            }

            const response = await axios.get(endpoint);

            if (response.data?.error) {
                console.error("API Error:", response.data.error);
                alert(`API Error: ${response.data.error}`);
                return;
            }

            console.log("Fetched data:", response.data);

            setGroupData(prevData => ({
                ...prevData,
                [groupType]: {
                    groups: Array.isArray(response.data.groups) ? response.data.groups : [],
                    unassignedStudents: Array.isArray(response.data.unassigned_students) ? response.data.unassigned_students : [],
                },
            }));
        } catch (error) {
            console.error(`Error fetching data for groupType=${groupType}:`, error);
            alert('Error fetching data. Please check if the backend API is running and responding with JSON.');
        }
    };

    useEffect(() => {
        if (currentGroupType) {
            fetchGradeLevelData(selectedGradeLevel, currentGroupType);
        }
    }, [selectedGradeLevel, currentGroupType]);


    const createGroups = async () => {
        const numGroups = parseInt(prompt('Enter the number of groups to create:'), 10);
        if (isNaN(numGroups) || numGroups <= 0) {
            alert('Please enter a valid number of groups.');
            return;
        }

        const csrfToken = getCSRFToken();
        const payload = {
            num_groups: numGroups,
            grade_level_id: Number(selectedGradeLevel),
        };

        try {
            let endpoint;
            switch (currentGroupType) {
                case 'core':
                    endpoint = '/api/groups/core-groups/create_groups/';
                    break;
                case 'cte':
                    endpoint = '/api/groups/cte-groups/create_groups/';
                    break;
                case 'homeroom':
                    endpoint = '/api/groups/homeroom-groups/create_groups/';
                    break;
                default:
                    console.error(`Unknown group type: ${currentGroupType}`);
                    return;
            }

            const response = await axios.post(endpoint, payload, {
                headers: {'X-CSRFToken': csrfToken},
            });
            console.log('Groups created:', response.data);
            fetchGradeLevelData(selectedGradeLevel, currentGroupType);
        } catch (error) {
            console.error('Error creating groups:', error);
            alert('Failed to create groups.');
        }
    };

    const addGroupManually = () => {
        if (!newGroupName.trim()) {
            alert('Group name cannot be empty.');
            return;
        }

        if (!selectedGradeLevel) {
            alert('Please select a grade level first.');
            return;
        }

        let endpoint;
        let payload;

        switch (currentGroupType) {
            case 'core': {
                endpoint = '/api/groups/core-groups/add/';
                payload = {
                    name: newGroupName,
                    grade_level_id: Number(selectedGradeLevel),
                };
                break;
            }
            case 'cte': {
                endpoint = '/api/groups/cte-groups/add/';
                const teacherId = prompt('Enter CTE teacher ID for this group:');
                if (!teacherId) {
                    alert('CTE teacher is required to create a CTE group.');
                    return;
                }
                payload = {
                    name: newGroupName,
                    grade_level: Number(selectedGradeLevel),
                    cte_teacher: Number(teacherId),
                };
                break;
            }
            case 'homeroom': {
                endpoint = '/api/groups/homeroom-groups/add/';
                const teacherId = prompt('Enter Homeroom teacher ID for this group:');
                if (!teacherId) {
                    alert('Homeroom teacher is required to create a Homeroom group.');
                    return;
                }
                payload = {
                    name: newGroupName,
                    grade_level: Number(selectedGradeLevel),
                    homeroom_teacher: Number(teacherId),
                };
                break;
            }
            default:
                console.error(`Unknown group type: ${currentGroupType}`);
                return;
        }

        console.log('addGroupManually POST', { endpoint, payload, currentGroupType });
        axios.post(endpoint, payload, {
            headers: {'X-CSRFToken': getCSRFToken()},
        })
            .then(response => {
                console.log('Group added:', response.data);
                setNewGroupName('');
                fetchGradeLevelData(selectedGradeLevel, currentGroupType);
            })
            .catch(error => {
                const detail = error?.response?.data ? JSON.stringify(error.response.data) : error.message;
                console.error('Error adding group:', detail);
                alert('Failed to add group: ' + detail);
            });
    };


    const handleDrop = async (item, targetGroup) => {
        console.log('Dragged item:', item);
        console.log('Target group:', targetGroup);

        try {
            console.log('Current groupData:', groupData);

            // Snapshot context to avoid races if user switches tabs/grade mid-op
            const opGroupType = currentGroupType;
            const opGradeLevel = selectedGradeLevel;

            if (!opGroupType) {
                console.error(`Error: Could not determine groupType for groupId ${item.groupId}`);
                return;
            }

            console.log('Using currentGroupType for operation:', opGroupType, 'grade:', opGradeLevel);

            if (!targetGroup) {
                console.log('Moving student to unassigned list');
                
                // Use type-specific endpoint for membership update
                let endpoint;
                switch (opGroupType) {
                    case 'core':
                        endpoint = '/api/groups/core-groups/update-membership/';
                        break;
                    case 'cte':
                        endpoint = '/api/groups/cte-groups/update-membership/';
                        break;
                    case 'homeroom':
                        endpoint = '/api/groups/homeroom-groups/update-membership/';
                        break;
                    default:
                        console.error(`Unknown group type: ${opGroupType}`);
                        return;
                }

                await axios.post(endpoint, {
                    student_id: item.id,
                    group_id: null, // moving to unassigned
                    grade_level: Number(opGradeLevel),
                }, {
                    headers: {'X-CSRFToken': getCSRFToken()},
                });

                setGroupData((prevGroupData) => {
                    // find student object
                    const student = (prevGroupData[opGroupType]?.unassignedStudents || []).find(s => s && String(s.id) === String(item.id))
                        || (prevGroupData[opGroupType]?.groups || []).flatMap(g => g.students || []).find(s => s && String(s.id) === String(item.id));

                // remove from any group
                const updatedGroups = (prevGroupData[opGroupType]?.groups || []).map(group => {
                    const contains = (group.students || []).some(s => s && String(s.id) === String(item.id));
                    return contains ? { ...group, students: (group.students || []).filter(s => s && String(s.id) !== String(item.id)) } : group;
                });

                // add to unassigned safely
                const baseUnassigned = (prevGroupData[opGroupType]?.unassignedStudents || []).filter(Boolean);
                const already = baseUnassigned.some(s => String(s.id) === String(item.id));
                const updatedUnassigned = already || !student ? baseUnassigned : [...baseUnassigned, student];

                return {
                    ...prevGroupData,
                    [opGroupType]: {
                        groups: updatedGroups,
                        unassignedStudents: updatedUnassigned,
                    },
                };
            });

                // Ensure UI syncs with backend
                await fetchGradeLevelData(opGradeLevel, opGroupType);

                console.log('Student successfully moved to unassigned list.');
                return;
            }

        // Use type-specific endpoint for membership update
        let endpoint;
        switch (opGroupType) {
            case 'core':
                endpoint = '/api/groups/core-groups/update-membership/';
                break;
            case 'cte':
                endpoint = '/api/groups/cte-groups/update-membership/';
                break;
            case 'homeroom':
                endpoint = '/api/groups/homeroom-groups/update-membership/';
                break;
            default:
                console.error(`Unknown group type: ${opGroupType}`);
                return;
        }
        
        await axios.post(endpoint, {
            student_id: item.id,
            group_id: targetGroup?.id || null, // Use group_id and handle unassigned (null)
            grade_level: Number(opGradeLevel), // Add required grade_level parameter
        }, {
            headers: {'X-CSRFToken': getCSRFToken()},
        });

        // Update state locally: remove from any group, add to target group, remove from unassigned
        setGroupData((prevGroupData) => {
            const student = (prevGroupData[opGroupType]?.unassignedStudents || []).find(s => s && String(s.id) === String(item.id))
                || (prevGroupData[opGroupType]?.groups || []).flatMap(g => g.students || []).find(s => s && String(s.id) === String(item.id));

            if (!student) return prevGroupData;

            const updatedGroups = (prevGroupData[opGroupType]?.groups || []).map(group => {
                const without = (group.students || []).filter(s => s && String(s.id) !== String(item.id));
                const shouldAdd = targetGroup?.id && String(group.id) === String(targetGroup.id);
                return { ...group, students: shouldAdd ? [...without, student] : without };
            });

            const updatedUnassigned = (prevGroupData[opGroupType]?.unassignedStudents || [])
                .filter(Boolean)
                .filter(s => String(s.id) !== String(item.id));

            return {
                ...prevGroupData,
                [opGroupType]: {
                    groups: updatedGroups,
                    unassignedStudents: updatedUnassigned,
                },
            };
        });

        console.log('Student successfully moved between groups.');
        // Re-fetch to ensure UI matches backend
        await fetchGradeLevelData(opGradeLevel, opGroupType);
    } catch (error) {
        console.error('Error during handleDrop:', error.message);
        alert('Error updating group membership: ' + error.message);
    }
};


    return (
        <>
            <DndProvider backend={HTML5Backend}>
                <div className="groups-home">
                    <h2 className="title">Manage Groups</h2>

                <div className="top-section">
                    <div className="filter-group">
                        <label htmlFor="grade-level-select">Grade Level:</label>
                        <select
                            id="grade-level-select"
                            value={selectedGradeLevel}
                            onChange={(e) => setSelectedGradeLevel(e.target.value)}

                        >
                            <option value="">--Select Grade Level--</option>
                            {gradeLevels.map(grade => (
                                <option key={grade.id} value={grade.id}>{grade.grade}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label htmlFor="group-type-select">Group Type:</label>
                        <select
                            id="group-type-select"
                            value={currentGroupType}
                            onChange={(e) => setCurrentGroupType(e.target.value)}
                        >
                            <option value="core">Core Classes</option>
                            <option value="cte">CTE Groups</option>
                            <option value="homeroom">Homeroom Groups</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <button onClick={createGroups}>Auto-Create Groups</button>
                        <button 
                            onClick={() => setShowImportModal(true)}
                            className="import-btn"
                            disabled={!selectedGradeLevel}
                        >
                            📥 Import Groups
                        </button>
                        <input
                            type="text"
                            value={newGroupName}
                            onChange={(e) => setNewGroupName(e.target.value)}
                            placeholder="Enter new group name"
                        />
                        <button onClick={addGroupManually}>Add Group</button>
                    </div>
                </div>

                <div className="layout">
                    <UnassignedStudents
                        students={groupData[currentGroupType]?.unassignedStudents || []}
                        onDrop={handleDrop}
                    />

                    <div className="group-list">
                        {console.log('Render:', {
                            currentGroupType,
                            selectedGradeLevel,
                            groupsCount: (groupData[currentGroupType]?.groups || []).length,
                            unassignedCount: (groupData[currentGroupType]?.unassignedStudents || []).length,
                        })}
                        {(groupData[currentGroupType]?.groups || []).map(group => (
                            <div key={group.id} className="group-container">
                                <div className="group-header">
                                    <h3>{group.name}</h3>
                                    <RemoveGroup
                                        groupId={group.id}
                                        groupName={group.name}
                                        groupType={currentGroupType}
                                        onRemove={(removedGroupId, removedStudents) => {
                                            setGroupData(prevGroupData => {
                                                const updatedGroups = prevGroupData[currentGroupType]?.groups?.filter(
                                                    group => group.id !== removedGroupId
                                                ) || [];

                                                const updatedUnassignedStudents = [
                                                    ...prevGroupData[currentGroupType]?.unassignedStudents || [],
                                                    ...removedStudents?.filter(student => student && student.id).map(student => ({
                                                        id: student.id,
                                                        first_name: student.first_name,
                                                        last_name: student.last_name,
                                                    })) || []
                                                ];

                                                return {
                                                    ...prevGroupData,
                                                    [currentGroupType]: {
                                                        groups: updatedGroups,
                                                        unassignedStudents: updatedUnassignedStudents,
                                                    },
                                                };
                                            });
                                        }}
                                    />
                                </div>
                                {/* Droppable Group */}
                                <DroppableGroup
                                    key={group.id}
                                    group={group}
                                    onDrop={handleDrop}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </DndProvider>

            {/* Import Modal */}
            {showImportModal && (
                <GroupImportModal
                    onClose={() => setShowImportModal(false)}
                    onImportComplete={(results) => {
                        // Refresh the group data after successful import
                        fetchGradeLevelData(selectedGradeLevel, currentGroupType);
                        setShowImportModal(false);
                    }}
                    gradeLevel={selectedGradeLevel}
                    groupType={currentGroupType}
                />
            )}
        </>
    );
};

export default ManageGroups;
